import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * Thin, AuthGuard-protected host for the standalone annotation tool.
 *
 * Why this exists:
 *  - The annotation tool is a full standalone HTML document with inline
 *    <script> logic. Angular never executes <script> inside a component
 *    template, so it cannot be rendered as a normal component.
 *  - A static asset under /assets is served directly by the web server and
 *    never passes through Angular's router, so it cannot be protected by
 *    AuthGuard either.
 *
 * Solution: route /annotation through this component (guarded by AuthGuard)
 * and load the real HTML in a same-origin <iframe>, where its inline scripts
 * run natively. This component acts purely as a message relay between the
 * iframe and the actual host window (browser opener or Windows WebView).
 */
@Component({
  selector: 'app-annotation-host',
  template: `
    <iframe #frame
            [src]="safeUrl"
            title="Annotation Tool"
            style="border:0; width:100vw; height:100vh; display:block;"></iframe>
  `
})
export class AnnotationHostComponent implements OnInit, OnDestroy {

  @ViewChild('frame', { static: true }) frame: ElementRef<HTMLIFrameElement>;

  safeUrl: SafeResourceUrl;
  private messageHandler: (event: MessageEvent) => void;

  constructor(private route: ActivatedRoute, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    const dataModelKey = this.route.snapshot.queryParamMap.get('dataModelKey') || '';
    // Relative path keeps it correct across environments/origins.
    const url = `assets/htmls/annotations.html?dataModelKey=${encodeURIComponent(dataModelKey)}`;
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    this.messageHandler = (event: MessageEvent) => {
      // Only trust messages from our own origin.
      if (event.origin !== window.location.origin) {
        return;
      }
      const msg = event.data;
      if (!msg || !msg.type) {
        return;
      }

      const iframeWin = this.frame && this.frame.nativeElement
        ? this.frame.nativeElement.contentWindow
        : null;

      // 1. Annotation add/edit/delete coming UP from the iframe → route it to
      //    the actual host (browser opener or Windows WebView).
      if (msg.type === 'ANNOTATION_ACTION' && event.source === iframeWin) {
        this.routeActionToHost(msg.payload);
        return;
      }

      // 2. Refresh pushed DOWN from the opener window (e.g. after a save) →
      //    forward it into the iframe so the table redraws. Ignore messages
      //    that originated from the iframe itself to avoid loops.
      if (msg.type === 'REFRESH_TABLE_DATA' && event.source !== iframeWin && iframeWin) {
        iframeWin.postMessage(msg, window.location.origin);
      }
    };

    window.addEventListener('message', this.messageHandler);
  }

  private routeActionToHost(payload: any): void {
    const w = window as any;

    // Windows WebView host (top frame owns chrome.webview).
    if (w.chrome && w.chrome.webview && w.chrome.webview.postMessage) {
      w.chrome.webview.postMessage(
        typeof payload === 'string' ? payload : JSON.stringify(payload)
      );
      return;
    }

    // Classic browser popup: the opener exposes onAnnotationSaved.
    if (window.opener && (window.opener as any).onAnnotationSaved) {
      (window.opener as any).onAnnotationSaved(payload);
      return;
    }

    console.warn('AnnotationHost: no host channel available for action', payload);
  }

  ngOnDestroy(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
  }
}
